import React from 'react';
import {connect} from 'react-redux';
import util from 'Utils/commonUtil';
import BaseImageUploader from 'Components/common/BaseImageUploader';
import BasicButton from 'Components/common/BasicButton';
import {openNotification} from 'Actions/notification';
import {
    uploadReceiveImage,
    updateOtherImages,
    deleteShipmentAttachment,
} from 'Actions/host';
import {T} from 'Actions/translate';

const MAX_OTHER_IMAGE_COUNT = 10;

class EditOtherPhotosDialog extends React.Component {
    otherImageInputRef = React.createRef();
    submitPending = false;
    otherImagesPreviewPending = false;

    state = {
        otherImages: [],
        selectedOtherImageId: '',
        isLoading: false,
        otherUploading: false,
    };

    componentDidMount() {
        this.resetState();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.opened && this.props.opened) {
            this.resetState();
        }
    }

    componentWillUnmount() {
        this.submitPending = false;
        this.otherImagesPreviewPending = false;
    }

    buildInitialOtherImages = () => {
        const {initialOtherImages = []} = this.props;
        return initialOtherImages.map((img, index) => ({
            id: img.id || `${Date.now()}_${index}_${Math.random()}`,
            file: null,
            url: img.imageUrl || img.url || '',
            persistedUrl: img.fileKey || img.imageUrl || img.url || '',
            status: 'success',
        }));
    };

    resetState = () => {
        this.submitPending = false;
        this.otherImagesPreviewPending = false;
        if (this.otherImageInputRef.current) {
            this.otherImageInputRef.current.value = '';
        }
        const otherImages = this.buildInitialOtherImages();
        this.setState({
            otherImages,
            selectedOtherImageId: otherImages[0] ? otherImages[0].id : '',
            isLoading: false,
            otherUploading: false,
        });
    };

    isInteractionLocked = () =>
        this.state.isLoading || this.submitPending || this.state.otherUploading;

    handleChooseImages = () => {
        if (this.isInteractionLocked() || this.otherImagesPreviewPending)
            return;
        if (this.state.otherImages.length >= MAX_OTHER_IMAGE_COUNT) {
            this.props.openNotification(
                T('shippment_upload_other_photos_limit', [
                    MAX_OTHER_IMAGE_COUNT,
                ]),
                'danger',
            );
            return;
        }
        if (this.otherImageInputRef.current) {
            this.otherImageInputRef.current.click();
        }
    };

    readFilePreview = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    handleSelectedImages = async (event) => {
        if (this.isInteractionLocked() || this.otherImagesPreviewPending) {
            if (this.otherImageInputRef.current) {
                this.otherImageInputRef.current.value = '';
            }
            return;
        }
        const selectedFiles = Array.from(
            (event.target && event.target.files) || [],
        );
        if (!selectedFiles.length) return;

        const restCount = MAX_OTHER_IMAGE_COUNT - this.state.otherImages.length;
        const availableFiles = selectedFiles.slice(0, restCount);

        const invalidFile = availableFiles.find(
            (f) => !/^image\//.test(f.type),
        );
        if (invalidFile) {
            this.props.openNotification(
                T('notification_please_select_picture'),
                'danger',
            );
            if (this.otherImageInputRef.current) {
                this.otherImageInputRef.current.value = '';
            }
            return;
        }

        this.otherImagesPreviewPending = true;
        let appendedImages = [];
        try {
            const previewUrls = await Promise.all(
                availableFiles.map((f) => this.readFilePreview(f)),
            );
            appendedImages = previewUrls.map((url, i) => ({
                id: `${Date.now()}_${i}_${Math.random()}`,
                file: availableFiles[i],
                url,
                persistedUrl: '',
                status: 'uploading',
            }));
            let exceeded = selectedFiles.length > restCount;
            this.setState((prev) => {
                const nextRest =
                    MAX_OTHER_IMAGE_COUNT - prev.otherImages.length;
                const safe = appendedImages.slice(0, Math.max(nextRest, 0));
                if (safe.length < appendedImages.length) {
                    exceeded = true;
                    appendedImages = safe;
                }
                const next = prev.otherImages.concat(safe);
                return {
                    otherImages: next,
                    selectedOtherImageId:
                        prev.selectedOtherImageId ||
                        (safe[0] ? safe[0].id : ''),
                    otherUploading: true,
                };
            });
            if (exceeded) {
                this.props.openNotification(
                    T('shippment_upload_other_photos_limit', [
                        MAX_OTHER_IMAGE_COUNT,
                    ]),
                    'danger',
                );
            }
        } finally {
            this.otherImagesPreviewPending = false;
            if (this.otherImageInputRef.current) {
                this.otherImageInputRef.current.value = '';
            }
        }

        // Upload each new image immediately after preview is shown
        for (const img of appendedImages) {
            try {
                const res = await this.props.uploadReceiveImage({
                    type: 'receipt_other_image',
                    file: img.file,
                });
                // Extract relative S3 key from presigned URL so backend
                // stores only the key and can regenerate signed URLs later.
                const fileKey = (() => {
                    try {
                        const pathname = new URL(res.url).pathname;
                        return pathname.startsWith('/')
                            ? pathname.slice(1)
                            : pathname;
                    } catch (_) {
                        return res.url;
                    }
                })();
                this.setState((prev) => ({
                    otherImages: prev.otherImages.map((o) =>
                        o.id === img.id
                            ? {
                                  ...o,
                                  persistedUrl: fileKey,
                                  file: null,
                                  status: 'success',
                              }
                            : o,
                    ),
                }));
            } catch (err) {
                this.updateImageStatus(img.id, 'error');
            }
        }
        this.setState({otherUploading: false});
    };

    getRelativeKey = (url) => {
        if (!url) return '';
        try {
            const pathname = new URL(url).pathname;
            return pathname.startsWith('/') ? pathname.slice(1) : pathname;
        } catch (_) {
            return url;
        }
    };

    getPersistedImageFileKey = (image) => {
        const persistedUrl = image && image.persistedUrl;
        if (!persistedUrl || typeof persistedUrl !== 'string') {
            return '';
        }

        const normalizedUrl = persistedUrl.trim();
        if (
            !normalizedUrl ||
            /^data:/i.test(normalizedUrl) ||
            /^blob:/i.test(normalizedUrl)
        ) {
            return '';
        }

        return this.getRelativeKey(normalizedUrl);
    };

    removeImageFromState = (imageId) => {
        this.setState((prev) => {
            const next = prev.otherImages.filter((img) => img.id !== imageId);
            return {
                otherImages: next,
                selectedOtherImageId:
                    prev.selectedOtherImageId === imageId
                        ? next[0]
                            ? next[0].id
                            : ''
                        : prev.selectedOtherImageId,
            };
        });
    };

    isDeleteAttachmentBlankError = (err) => {
        const message = `${(err && err.message) || ''} ${
            (err && err.status) || ''
        }`.toLowerCase();

        return (
            message.indexOf('modeid or filekey is blank') > -1 ||
            (message.indexOf('modeid') > -1 &&
                message.indexOf('filekey') > -1 &&
                message.indexOf('blank') > -1)
        );
    };

    handleRemoveImage = async (imageId) => {
        if (this.isInteractionLocked()) return;

        const image = this.state.otherImages.find((img) => img.id === imageId);
        if (!image) return;

        const fileKey = this.getPersistedImageFileKey(image);
        if (!fileKey) {
            this.removeImageFromState(imageId);
            return;
        }

        const {shipmentId} = this.props;
        try {
            await this.props.deleteShipmentAttachment({
                modeId: shipmentId,
                fileKey,
            });
            this.removeImageFromState(imageId);
        } catch (err) {
            if (this.isDeleteAttachmentBlankError(err)) {
                this.props.openNotification(
                    'modeId或fileKey不能为空 / modeId or fileKey is blank',
                    'danger',
                );
                return;
            }

            this.props.openNotification(
                '删除图片失败 / Failed to delete image',
                'danger',
            );
        }
    };

    handleSelectImage = (selectedOtherImageId) => {
        if (this.isInteractionLocked()) return;
        this.setState({selectedOtherImageId});
    };

    updateImageStatus = (imageId, status) => {
        this.setState((prev) => ({
            otherImages: prev.otherImages.map((img) =>
                img.id === imageId ? {...img, status} : img,
            ),
        }));
    };

    handleSubmit = async () => {
        if (this.isInteractionLocked()) return;
        const {shipmentId, receiveType, onSuccess} = this.props;
        const {otherImages} = this.state;

        this.submitPending = true;
        this.setState({isLoading: true});
        try {
            // Normalize all URLs to S3 relative keys so backend stores stable keys
            const extractRelativeKey = (url) => {
                if (!url) return url;
                try {
                    const pathname = new URL(url).pathname;
                    return pathname.startsWith('/')
                        ? pathname.slice(1)
                        : pathname;
                } catch (_) {
                    return url; // already a relative path
                }
            };
            const otherImageUrls = otherImages
                .filter(
                    (img) =>
                        img.status === 'success' &&
                        (img.persistedUrl || img.url),
                )
                .map((img) => extractRelativeKey(img.persistedUrl || img.url));

            await this.props.updateOtherImages({
                shipmentId,
                receiveType,
                otherImageUrls,
            });
            if (typeof onSuccess === 'function') onSuccess();
        } finally {
            this.submitPending = false;
            this.setState({isLoading: false});
        }
    };

    render() {
        const {onClose} = this.props;
        const {otherImages, selectedOtherImageId, isLoading, otherUploading} =
            this.state;
        const locked = this.isInteractionLocked();

        const selectedImage =
            otherImages.find((img) => img.id === selectedOtherImageId) ||
            otherImages[0];

        return (
            <div className="upload-image-dialog other-photo-receive-dialog edit-other-photos-dialog">
                <h5 className="upload-method-title text-center">
                    {T('shippment_upload_other_photos')}
                </h5>
                <div className="signature-method-section">
                    <div className="signature-method-section-header">
                        <span className="signature-method-section-text">
                            {T('shippment_upload_other_photos')}
                        </span>
                    </div>
                    {otherImages.length === 0 ? (
                        <BaseImageUploader
                            className="other-photo-upload-empty"
                            disabled={locked}
                            tag="button"
                            type="button"
                            isPreventDefault={false}
                            onClickOfSingleTap={this.handleChooseImages}>
                            <span className="other-photo-upload-plus">+</span>
                            <span className="other-photo-upload-empty-text">
                                {T('shippment_upload_other_photos_here')}
                            </span>
                        </BaseImageUploader>
                    ) : (
                        <div className="other-photo-upload-filled">
                            <div className="other-photo-upload-count">
                                {T('shippment_upload_other_photos_selected', [
                                    otherImages.length,
                                    MAX_OTHER_IMAGE_COUNT,
                                ])}
                            </div>
                            <div className="other-photo-upload-preview">
                                {selectedImage && (
                                    <img src={selectedImage.url} alt="" />
                                )}
                            </div>
                            <div className="other-photo-upload-grid">
                                {otherImages.map((img) => (
                                    <div
                                        className={util.getClassName({
                                            'other-photo-upload-item': true,
                                            'is-active':
                                                selectedImage &&
                                                selectedImage.id === img.id,
                                            'is-disabled': locked,
                                        })}
                                        key={img.id}
                                        onClick={() =>
                                            this.handleSelectImage(img.id)
                                        }>
                                        <img src={img.url} alt="" />
                                        <button
                                            type="button"
                                            className="other-photo-upload-remove"
                                            disabled={locked}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.handleRemoveImage(img.id);
                                            }}>
                                            ×
                                        </button>
                                        {img.status === 'uploading' ? (
                                            <span className="other-photo-upload-status">
                                                {T('loading')}
                                            </span>
                                        ) : null}
                                        {img.status === 'error' ? (
                                            <span className="other-photo-upload-status is-error">
                                                {T(
                                                    'shippment_upload_other_photos_failed',
                                                )}
                                            </span>
                                        ) : null}
                                    </div>
                                ))}
                                {otherImages.length < MAX_OTHER_IMAGE_COUNT ? (
                                    <button
                                        type="button"
                                        className="other-photo-upload-add"
                                        disabled={locked}
                                        onClick={this.handleChooseImages}>
                                        <span className="other-photo-upload-add-plus">
                                            +
                                        </span>
                                        <span className="other-photo-upload-add-text">
                                            {T(
                                                'shippment_upload_other_photos_add',
                                            )}
                                        </span>
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
                <input
                    ref={this.otherImageInputRef}
                    className="other-photo-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={this.handleSelectedImages}
                />
                <div className="upload-method-actions">
                    <BasicButton
                        type="primary"
                        className="upload-method-submit"
                        loading={isLoading || otherUploading}
                        disabled={locked}
                        onClickOfSingleTap={this.handleSubmit}>
                        <span>{T('shippment_confirm_upload')}</span>
                    </BasicButton>
                    <BasicButton
                        type="default"
                        className="upload-method-cancel"
                        disabled={locked}
                        onClickOfSingleTap={() => {
                            this.resetState();
                            onClose();
                        }}>
                        <span>{T('cancel')}</span>
                    </BasicButton>
                </div>
            </div>
        );
    }
}

EditOtherPhotosDialog.defaultProps = {
    initialOtherImages: [],
};

const mapDispatchToProps = (dispatch) => ({
    openNotification: (msg, type, opts) =>
        dispatch(openNotification(msg, type, opts)),
    uploadReceiveImage: (data) => dispatch(uploadReceiveImage(data)),
    updateOtherImages: (data) => dispatch(updateOtherImages(data)),
    deleteShipmentAttachment: (params) =>
        dispatch(deleteShipmentAttachment(params)),
});

export default connect(null, mapDispatchToProps)(EditOtherPhotosDialog);
